/**
 * Time Tracking Dashboard Component
 * 
 * Main visualization component for time tracking with Radar charts,
 * value metrics, and AI-powered insights.
 * 
 * @module components/timeTracking/TimeTrackingDashboard
 * @author SuperKonnected Team - Senior UI/UX Implementation
 * @version 1.0.0
 */

import React, { useState, useEffect, useMemo } from 'react';
import {
  RadarChart,
  Radar,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  ResponsiveContainer,
  Legend,
  Tooltip,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid
} from 'recharts';
import { timeTrackingService, getGoalTimeBreakdown, GoalTimeSummary } from '../../services/timeTrackingService';
import { DailyComparison } from '../../types/timeTracking';
import { Clock, TrendingUp, Target, AlertTriangle, Zap } from '../icons';
import { db } from '../../firebase';
import { collection, query, where, getDocs } from 'firebase/firestore';

// Types for tasks integration
type BalanceCategory = 'work' | 'personal' | 'self' | 'essential' | 'none' | 'family' | 'activity';

interface TaskListSummary {
  listId: string;
  listName: string;
  balanceCategory: BalanceCategory;
  completedCount: number;
  totalActiveTime: number; // ms
}

const balanceCategoryLabels: Record<BalanceCategory, { label: string; emoji: string }> = {
  work: { label: 'Werk', emoji: '💼' },
  personal: { label: 'Privé', emoji: '🏠' },
  self: { label: 'Zelf', emoji: '🧘' },
  essential: { label: 'Essentieel', emoji: '⚡' },
  family: { label: 'Familie', emoji: '👨‍👩‍👧‍👦' },
  activity: { label: 'Activiteit', emoji: '🎯' },
  none: { label: 'Niet meewegen', emoji: '❌' }
};

// ============================================================================
// TYPES
// ============================================================================

interface TimeTrackingDashboardProps {
  userId: string;
  userProfile: any;
  onRequestCheckOut?: () => void;
}

interface RadarData {
  activity: string;
  actual: number; // hours
  target: number; // hours
  maxValue: number;
}

interface ValueMetrics {
  efficiency: number; // 0-100
  highValueHours: number;
  totalHours: number;
  balanceScore: number;
}

// ============================================================================
// CONSTANTS
// ============================================================================

const IDEAL_WEEKLY_BALANCE = {
  work: { min: 35, ideal: 42, max: 50 },
  personal: { min: 20, ideal: 28, max: 35 },
  self: { min: 15, ideal: 21, max: 28 },
  essential: { min: 50, ideal: 56, max: 63 }
};

const COLORS = {
  work: '#4F6660',
  personal: '#4ECDC4',
  self: '#95E1D3',
  essential: '#0A2342',
  target: '#94A3B8'
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const TimeTrackingDashboard: React.FC<TimeTrackingDashboardProps> = ({
  userId,
  userProfile,
  onRequestCheckOut
}) => {
  // ============================================================================
  // STATE
  // ============================================================================

  const [loading, setLoading] = useState(true);
  const [dailyData, setDailyData] = useState<DailyComparison[]>([]);
  const [categoryTotals, setCategoryTotals] = useState({
    work: 0,
    personal: 0,
    self: 0,
    essential: 0,
    family: 0,
    activity: 0
  });
  const [weeklyBreakdown, setWeeklyBreakdown] = useState<any>({});
  const [error, setError] = useState<string | null>(null);

  // Taken Analyse state
  const [taskPeriod, setTaskPeriod] = useState<'week' | 'month' | 'year'>('month');
  const [taskListSummaries, setTaskListSummaries] = useState<TaskListSummary[]>([]);
  const [tasksLoading, setTasksLoading] = useState(false);
  const [countTasksInBalance, setCountTasksInBalance] = useState(false);

  // Doelstellingen Tijd Analyse state
  const [goalTimeSummaries, setGoalTimeSummaries] = useState<GoalTimeSummary[]>([]);
  const [goalsLoading, setGoalsLoading] = useState(false);

  // ============================================================================
  // EFFECTS
  // ============================================================================

  useEffect(() => {
    if (userId) {
      loadData();
      loadTaskData();
      loadGoalTimeData();
    }
  }, [userId]);

  useEffect(() => {
    if (userId) {
      loadTaskData();
    }
  }, [userId, taskPeriod]);

  useEffect(() => {
    if (userId) {
      loadGoalTimeData();
    }
  }, [userId, taskPeriod]);

  // ============================================================================
  // DATA LOADING
  // ============================================================================

  const loadData = async () => {
    setLoading(true);
    setError(null);

    try {
      const [daily, totals, breakdown] = await Promise.all([
        timeTrackingService.getDailyBreakdown(userId, 7),
        timeTrackingService.getCategoryTotals(userId),
        timeTrackingService.getWeeklyBreakdown(userId)
      ]);

      setDailyData(daily);
      setCategoryTotals(totals);
      setWeeklyBreakdown(breakdown);
    } catch (err) {
      console.error('Error loading time tracking data:', err);
      setError('Kon data niet laden. Probeer het later opnieuw.');
    } finally {
      setLoading(false);
    }
  };

  const loadTaskData = async () => {
    if (!userId) return;
    setTasksLoading(true);

    try {
      // Get task lists
      const listsQuery = query(
        collection(db, 'agendaTaskLists'),
        where('userId', '==', userId)
      );
      const listsSnapshot = await getDocs(listsQuery);
      const listsMap = new Map<string, { name: string; balanceCategory: BalanceCategory }>();
      
      // Default lists
      const defaultLists = [
        { id: 'ideas', name: 'Ideeën', balanceCategory: 'self' as BalanceCategory },
        { id: 'activities', name: 'Activiteiten', balanceCategory: 'work' as BalanceCategory },
        { id: 'actions', name: 'Acties', balanceCategory: 'work' as BalanceCategory },
        { id: 'private', name: 'Privé', balanceCategory: 'personal' as BalanceCategory },
        { id: 'goals', name: '🎯 Doelen Acties', balanceCategory: 'work' as BalanceCategory }
      ];
      
      defaultLists.forEach(l => listsMap.set(l.id, { name: l.name, balanceCategory: l.balanceCategory }));
      
      listsSnapshot.docs.forEach(doc => {
        const data = doc.data();
        // Gebruik alleen als er een leesbare naam is
        if (data.name) {
          listsMap.set(doc.id, { 
            name: data.name, 
            balanceCategory: data.balanceCategory || 'none' 
          });
        }
      });

      // Calculate period start date
      const now = new Date();
      let startDate: Date;
      if (taskPeriod === 'week') {
        startDate = new Date(now);
        startDate.setDate(now.getDate() - 7);
      } else if (taskPeriod === 'month') {
        startDate = new Date(now);
        startDate.setMonth(now.getMonth() - 1);
      } else {
        startDate = new Date(now);
        startDate.setFullYear(now.getFullYear() - 1);
      }

      // Get completed tasks
      const tasksQuery = query(
        collection(db, 'agendaTasks'),
        where('userId', '==', userId),
        where('completed', '==', true)
      );
      const tasksSnapshot = await getDocs(tasksQuery);

      // Group by list
      const summaryMap = new Map<string, TaskListSummary>();

      tasksSnapshot.docs.forEach(doc => {
        const task = doc.data();
        const completedAt = task.completedAt?.toDate?.() || null;
        
        if (!completedAt || completedAt < startDate) return;

        const listId = task.originalListId || task.listId;
        // Skip systeemlijsten en oude/verwijderde lijsten
        if (listId === 'done' || listId === 'all' || listId === 'planning') return;

        // Skip onbekende lijsten (Firebase IDs zonder naam)
        const listInfo = listsMap.get(listId);
        if (!listInfo) return; // Negeer taken van onbekende lijsten
        
        // Voor 'goals' taken: gebruik task-specifieke balanceCategory indien aanwezig
        // Dit maakt het mogelijk dat bijv. een sport-doel naar 'activity' telt ipv 'work'
        const effectiveCategory = (listId === 'goals' && task.balanceCategory) 
          ? task.balanceCategory 
          : listInfo.balanceCategory;
        
        // Gebruik effectiveCategory als key voor correcte groepering
        const summaryKey = listId === 'goals' && task.balanceCategory 
          ? `goals_${task.balanceCategory}` 
          : listId;
        
        if (!summaryMap.has(summaryKey)) {
          summaryMap.set(summaryKey, {
            listId: summaryKey,
            listName: listId === 'goals' && task.balanceCategory 
              ? `🎯 Doelen (${task.balanceCategory === 'work' ? 'Werk' : task.balanceCategory === 'self' ? 'Persoonlijk' : task.balanceCategory === 'family' ? 'Familie' : 'Activiteit'})`
              : listInfo.name,
            balanceCategory: effectiveCategory,
            completedCount: 0,
            totalActiveTime: 0
          });
        }

        const summary = summaryMap.get(summaryKey)!;
        summary.completedCount++;
        summary.totalActiveTime += task.activeTime || 0;
      });

      // === DOELSTELLINGEN ACTIES ===
      // Query completed actions from 'actions' collection (where Doelstellingen timer data is stored)
      // This ensures time tracked on Doelstellingen actions counts towards levensbalans
      const actionsQuery = query(
        collection(db, 'actions'),
        where('ownerId', '==', userId),
        where('completed', '==', true)
      );
      const actionsSnapshot = await getDocs(actionsQuery);
      
      // Get goals for balanceCategory lookup
      const goalsQuery = query(
        collection(db, 'goals'),
        where('ownerId', '==', userId)
      );
      const goalsSnapshot = await getDocs(goalsQuery);
      const goalsMap = new Map<string, { title: string; balanceCategory?: string }>();
      goalsSnapshot.docs.forEach(doc => {
        const goal = doc.data();
        goalsMap.set(doc.id, { 
          title: goal.title, 
          balanceCategory: goal.balanceCategory 
        });
      });
      
      // Add completed actions to summary
      actionsSnapshot.docs.forEach(doc => {
        const action = doc.data();
        const completedAt = action.completedAt ? new Date(action.completedAt) : null;
        
        if (!completedAt || completedAt < startDate) return;
        if (!action.activeTime || action.activeTime === 0) return; // Skip actions without tracked time
        
        // Get the goal's balanceCategory
        const goal = goalsMap.get(action.goalId);
        const balanceCategory = (goal?.balanceCategory || 'work') as BalanceCategory;
        
        // Create summary key based on balanceCategory
        const summaryKey = `goals_${balanceCategory}`;
        const categoryLabel = balanceCategory === 'work' ? 'Werk' 
          : balanceCategory === 'self' ? 'Persoonlijk' 
          : balanceCategory === 'family' ? 'Familie' 
          : 'Activiteit';
        
        if (!summaryMap.has(summaryKey)) {
          summaryMap.set(summaryKey, {
            listId: summaryKey,
            listName: `🎯 Doelen (${categoryLabel})`,
            balanceCategory: balanceCategory,
            completedCount: 0,
            totalActiveTime: 0
          });
        }
        
        const summary = summaryMap.get(summaryKey)!;
        summary.completedCount++;
        summary.totalActiveTime += action.activeTime || 0;
      });

      setTaskListSummaries(Array.from(summaryMap.values()).sort((a, b) => b.completedCount - a.completedCount));
    } catch (err) {
      console.error('Error loading task data:', err);
    } finally {
      setTasksLoading(false);
    }
  };

  const loadGoalTimeData = async () => {
    if (!userId) return;
    setGoalsLoading(true);

    try {
      // Calculate period start date
      const now = new Date();
      let startDate: Date;
      if (taskPeriod === 'week') {
        startDate = new Date(now);
        startDate.setDate(now.getDate() - 7);
      } else if (taskPeriod === 'month') {
        startDate = new Date(now);
        startDate.setMonth(now.getMonth() - 1);
      } else {
        startDate = new Date(now);
        startDate.setFullYear(now.getFullYear() - 1);
      }

      const summaries = await getGoalTimeBreakdown(userId, startDate, now);
      setGoalTimeSummaries(summaries);
    } catch (err) {
      console.error('Error loading goal time data:', err);
    } finally {
      setGoalsLoading(false);
    }
  };

  // ============================================================================
  // COMPUTED DATA
  // ============================================================================

  // Bereken gecombineerde totalen (inclusief taaktijd indien aangevinkt)
  const combinedTotals = useMemo(() => {
    const base = { ...categoryTotals };
    
    if (countTasksInBalance && taskListSummaries.length > 0) {
      taskListSummaries.forEach(summary => {
        if (summary.balanceCategory !== 'none' && summary.totalActiveTime > 0) {
          // Converteer ms naar uren
          const hours = summary.totalActiveTime / (1000 * 60 * 60);
          base[summary.balanceCategory] = (base[summary.balanceCategory] || 0) + hours;
        }
      });
    }
    
    return base;
  }, [categoryTotals, countTasksInBalance, taskListSummaries]);

  const radarData = useMemo((): RadarData[] => {
    // Essentieel geparkeerd - niet actief gemeten
    return [
      {
        activity: 'Werk',
        actual: combinedTotals.work,
        target: IDEAL_WEEKLY_BALANCE.work.ideal,
        maxValue: IDEAL_WEEKLY_BALANCE.work.max
      },
      {
        activity: 'Privé',
        actual: combinedTotals.personal,
        target: IDEAL_WEEKLY_BALANCE.personal.ideal,
        maxValue: IDEAL_WEEKLY_BALANCE.personal.max
      },
      {
        activity: 'Zelf',
        actual: combinedTotals.self,
        target: IDEAL_WEEKLY_BALANCE.self.ideal,
        maxValue: IDEAL_WEEKLY_BALANCE.self.max
      }
    ];
  }, [combinedTotals]);

  const valueMetrics = useMemo((): ValueMetrics => {
    const total = Object.values(combinedTotals).reduce((sum, h) => sum + h, 0);
    const workHours = combinedTotals.work;

    // Simple efficiency calculation (can be enhanced with value assessment later)
    const efficiency = total > 0 ? Math.min((workHours / total) * 100, 100) : 0;

    // Balance score (how close to ideal)
    const balanceScore = calculateBalanceScore(combinedTotals);

    return {
      efficiency: Math.round(efficiency),
      highValueHours: workHours * 0.6, // Estimate (60% of work is high-value)
      totalHours: total,
      balanceScore: Math.round(balanceScore)
    };
  }, [combinedTotals]);

  const insights = useMemo(() => {
    const messages: string[] = [];
    const warnings: string[] = [];

    // Check work hours
    if (combinedTotals.work > IDEAL_WEEKLY_BALANCE.work.max) {
      warnings.push(`⚠️ Je werkt ${Math.round(combinedTotals.work - IDEAL_WEEKLY_BALANCE.work.max)}u meer dan ideaal`);
    } else if (combinedTotals.work < IDEAL_WEEKLY_BALANCE.work.min) {
      messages.push(`💡 Je hebt ruimte voor ${Math.round(IDEAL_WEEKLY_BALANCE.work.min - combinedTotals.work)}u meer werk`);
    } else {
      messages.push('✅ Werk uren zijn in balans');
    }

    // Check personal time
    if (combinedTotals.personal < IDEAL_WEEKLY_BALANCE.personal.min) {
      warnings.push(`⚠️ Privé tijd ${Math.round(IDEAL_WEEKLY_BALANCE.personal.min - combinedTotals.personal)}u onder target`);
    }

    // Check self time
    if (combinedTotals.self < IDEAL_WEEKLY_BALANCE.self.min) {
      warnings.push(`⚠️ Zelf tijd te laag - blokkeer tijd voor jezelf`);
    }

    return { messages, warnings };
  }, [combinedTotals]);

  // ============================================================================
  // RENDER
  // ============================================================================

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-gray-200 rounded w-1/3"></div>
            <div className="h-64 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-6">
        <div className="flex items-center gap-3">
          <AlertTriangle className="text-red-600" size={24} />
          <div>
            <h3 className="font-bold text-red-900">Fout bij laden</h3>
            <p className="text-red-700 text-sm">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  const totalHours = Object.values(categoryTotals).reduce((sum, h) => sum + h, 0);
  const hasData = totalHours > 0;

  if (!hasData) {
    return (
      <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl p-8 border border-orange-200">
        <div className="text-center">
          <Clock size={48} className="mx-auto mb-4 text-orange-600" />
          <h3 className="text-xl font-bold text-gray-900 mb-2">
            Start met tracken
          </h3>
          <p className="text-gray-700 mb-6">
            Er is nog geen data beschikbaar. Begin met het bijhouden van je tijd om inzichten te krijgen.
          </p>
          {onRequestCheckOut && (
            <button
              onClick={onRequestCheckOut}
              className="px-6 py-3 bg-gradient-to-r from-orange-500 to-orange-600 text-white font-medium rounded-lg hover:shadow-lg transition-all"
            >
              Start Reflectie
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          label="Totaal deze week"
          value={`${Math.round(totalHours)}u`}
          icon={<Clock size={20} />}
          color="blue"
        />
        <StatCard
          label="Efficiency Score"
          value={`${valueMetrics.efficiency}%`}
          icon={<Zap size={20} />}
          color="orange"
        />
        <StatCard
          label="Balans Score"
          value={`${valueMetrics.balanceScore}/100`}
          icon={<Target size={20} />}
          color="green"
        />
        <StatCard
          label="Trend"
          value="+8%"
          icon={<TrendingUp size={20} />}
          color="purple"
        />
      </div>

      {/* Main Radar Chart */}
      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
        <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
          <span className="text-2xl">🕸️</span>
          Levensbalans Overzicht
          {countTasksInBalance && taskListSummaries.some(s => s.totalActiveTime > 0) && (
            <span className="ml-2 text-xs font-normal bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full">
              + taaktijd
            </span>
          )}
        </h3>

        <ResponsiveContainer width="100%" height={400}>
          <RadarChart data={radarData}>
            <PolarGrid stroke="#e5e7eb" />
            {/* @ts-ignore - recharts type compat with React 18 */}
            <PolarAngleAxis
              dataKey="activity"
              tick={{ fill: '#374151', fontSize: 14, fontWeight: 500 }}
            />
            {/* @ts-ignore - recharts type compat with React 18 */}
            <PolarRadiusAxis
              angle={90}
              domain={[0, 60]}
              tick={{ fill: '#6b7280', fontSize: 12 }}
            />
            
            {/* Target (dotted) */}
            <Radar
              name="Target"
              dataKey="target"
              stroke={COLORS.target}
              fill={COLORS.target}
              fillOpacity={0.2}
              strokeDasharray="5 5"
              strokeWidth={2}
            />
            
            {/* Actual (solid) */}
            <Radar
              name="Jouw Week"
              dataKey="actual"
              stroke={COLORS.work}
              fill={COLORS.work}
              fillOpacity={0.6}
              strokeWidth={2}
            />
            
            <Legend
              wrapperStyle={{ paddingTop: '20px' }}
              iconType="circle"
            />
            <Tooltip
              contentStyle={{
                backgroundColor: 'white',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                padding: '12px'
              }}
              formatter={(value: number | undefined) => value ? `${Math.round(value)}u` : '0u'}
            />
          </RadarChart>
        </ResponsiveContainer>

        {/* Legend explanation */}
        <div className="mt-4 flex flex-wrap gap-4 justify-center text-sm">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-orange-500"></div>
            <span className="text-gray-700">Jouw tijd</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-gray-400 border-2 border-dashed border-gray-400"></div>
            <span className="text-gray-700">Ideaal target</span>
          </div>
        </div>
      </div>

      {/* Category Breakdown */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <CategoryCard
          label="💼 Werk"
          hours={combinedTotals.work}
          target={IDEAL_WEEKLY_BALANCE.work.ideal}
          color={COLORS.work}
        />
        <CategoryCard
          label="🏠 Privé"
          hours={combinedTotals.personal}
          target={IDEAL_WEEKLY_BALANCE.personal.ideal}
          color={COLORS.personal}
        />
        <CategoryCard
          label="🧘 Zelf"
          hours={combinedTotals.self}
          target={IDEAL_WEEKLY_BALANCE.self.ideal}
          color={COLORS.self}
        />
        {/* Essentieel geparkeerd - niet actief gemeten */}
      </div>

      {/* Weekly Bar Chart */}
      {dailyData.length > 0 && (
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <h3 className="text-xl font-bold text-gray-900 mb-6">
            📅 Week Overzicht
          </h3>

          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={dailyData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="dayName" tick={{ fill: '#6b7280', fontSize: 12 }} />
              <YAxis tick={{ fill: '#6b7280', fontSize: 12 }} label={{ value: 'Uren', angle: -90, position: 'insideLeft' }} />
              <Tooltip
                contentStyle={{
                  backgroundColor: 'white',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  padding: '12px'
                }}
                formatter={(value: number | undefined) => value ? `${value.toFixed(1)}u` : '0.0u'}
              />
              <Legend />
              <Bar dataKey="work" name="Werk" fill={COLORS.work} radius={[4, 4, 0, 0]} />
              <Bar dataKey="personal" name="Privé" fill={COLORS.personal} radius={[4, 4, 0, 0]} />
              <Bar dataKey="self" name="Zelf" fill={COLORS.self} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Taken Analyse */}
      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
        <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
          <span className="text-2xl">📋</span>
          Taken Analyse
        </h3>

        {/* Periode selector */}
        <div className="flex gap-2 mb-4">
          {(['week', 'month', 'year'] as const).map(p => (
            <button
              key={p}
              onClick={() => setTaskPeriod(p)}
              className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
                taskPeriod === p 
                  ? 'bg-gray-800 text-white' 
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {p === 'week' ? 'Week' : p === 'month' ? 'Maand' : 'Jaar'}
            </button>
          ))}
        </div>

        {tasksLoading ? (
          <div className="text-center py-8 text-gray-500">Laden...</div>
        ) : taskListSummaries.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            Geen voltooide taken in deze periode.
          </div>
        ) : (
          <>
            {/* Totalen */}
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="text-sm text-gray-600 mb-1">Taken voltooid</div>
                <div className="text-2xl font-bold text-gray-900">
                  {taskListSummaries.reduce((sum, s) => sum + s.completedCount, 0)}
                </div>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="text-sm text-gray-600 mb-1">Actieve tijd</div>
                <div className="text-2xl font-bold text-gray-900">
                  {formatTaskTime(taskListSummaries.reduce((sum, s) => sum + s.totalActiveTime, 0))}
                </div>
              </div>
            </div>

            {/* Per lijst */}
            <div className="space-y-2 mb-4">
              {taskListSummaries.map(summary => (
                <div 
                  key={summary.listId}
                  className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded-lg"
                >
                  <div className="flex items-center gap-2">
                    <span className="text-sm">
                      {balanceCategoryLabels[summary.balanceCategory]?.emoji || '📁'}
                    </span>
                    <span className="text-sm font-medium text-gray-800">
                      {summary.listName}
                    </span>
                    <span className="text-xs text-gray-500 bg-gray-200 px-1.5 py-0.5 rounded">
                      {balanceCategoryLabels[summary.balanceCategory]?.label || 'Overig'}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 text-sm">
                    <span className="text-gray-600">{summary.completedCount} taken</span>
                    {summary.totalActiveTime > 0 && (
                      <span className="text-orange-600 font-medium">
                        {formatTaskTime(summary.totalActiveTime)}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Toggle voor levensbalans */}
            <div className="flex items-center gap-3 pt-4 border-t border-gray-200">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={countTasksInBalance}
                  onChange={e => setCountTasksInBalance(e.target.checked)}
                  className="w-4 h-4 rounded border-gray-300 text-orange-500 focus:ring-orange-500"
                />
                <span className="text-sm text-gray-700">Tel mee in Levensbalans</span>
              </label>
              {countTasksInBalance && (
                <span className="text-xs text-gray-500">
                  (actieve tijd wordt opgeteld bij de categorieën)
                </span>
              )}
            </div>
          </>
        )}
      </div>

      {/* Tijd per Doel - Doelstellingen Analytics */}
      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
        <h3 className="text-xl font-bold text-gray-900 mb-4">
          🎯 Tijd per Doel ({taskPeriod === 'week' ? 'deze week' : taskPeriod === 'month' ? 'deze maand' : 'dit jaar'})
        </h3>

        {goalsLoading ? (
          <div className="text-center py-8 text-gray-500">Laden...</div>
        ) : goalTimeSummaries.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            Nog geen tijd besteed aan doelen deze periode.
          </div>
        ) : (
          <>
            <div className="space-y-2">
              {goalTimeSummaries.map(goal => {
                const maxHours = Math.max(...goalTimeSummaries.map(g => g.totalHours));
                const barWidth = maxHours > 0 ? (goal.totalHours / maxHours) * 100 : 0;
                
                return (
                  <div key={goal.goalId} className="flex items-center gap-3">
                    {/* Goal naam */}
                    <span className="text-xs text-gray-700 flex-1 truncate min-w-0">
                      {goal.goalTitle}
                    </span>
                    
                    {/* Bar chart */}
                    <div className="flex-1 min-w-0">
                      <div className="h-4 bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-blue-500 transition-all duration-300"
                          style={{ width: `${barWidth}%` }}
                        />
                      </div>
                    </div>
                    
                    {/* Tijd display */}
                    <span className="text-xs font-medium text-gray-800 w-12 text-right flex-shrink-0">
                      {goal.totalHours.toFixed(1)}u
                    </span>
                  </div>
                );
              })}
            </div>

            {/* Totaal */}
            <div className="mt-4 pt-4 border-t border-gray-200 flex justify-between items-center text-sm">
              <span className="text-gray-600">Totaal aan doelen:</span>
              <span className="font-semibold text-gray-800 text-base">
                {goalTimeSummaries.reduce((sum, g) => sum + g.totalHours, 0).toFixed(1)}u
              </span>
            </div>
          </>
        )}
      </div>

      {/* Insights */}
      <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-6 border border-blue-200">
        <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
          <span className="text-2xl">💡</span>
          AI Insights & Aanbevelingen
        </h3>

        <div className="space-y-3">
          {insights.messages.map((msg, idx) => (
            <div key={idx} className="flex items-start gap-3 text-gray-800">
              <span>{msg}</span>
            </div>
          ))}
          {insights.warnings.map((warning, idx) => (
            <div key={idx} className="flex items-start gap-3 text-orange-700 font-medium">
              <span>{warning}</span>
            </div>
          ))}
        </div>

        {onRequestCheckOut && (
          <button
            onClick={onRequestCheckOut}
            className="mt-4 w-full sm:w-auto px-6 py-3 bg-white text-blue-700 font-medium rounded-lg hover:shadow-md transition-all border border-blue-300"
          >
            💭 Doe dagelijkse reflectie
          </button>
        )}
      </div>
    </div>
  );
};

// ============================================================================
// SUB-COMPONENTS
// ============================================================================

interface StatCardProps {
  label: string;
  value: string;
  icon: React.ReactNode;
  color: 'blue' | 'orange' | 'green' | 'purple';
}

const StatCard: React.FC<StatCardProps> = ({ label, value, icon, color }) => {
  const colorClasses = {
    blue: 'from-blue-500 to-blue-600',
    orange: 'from-orange-500 to-orange-600',
    green: 'from-green-500 to-green-600',
    purple: 'from-purple-500 to-purple-600'
  };

  return (
    <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
      <div className={`inline-flex items-center justify-center w-10 h-10 rounded-lg bg-gradient-to-br ${colorClasses[color]} text-white mb-3`}>
        {icon}
      </div>
      <div className="text-sm text-gray-600 mb-1">{label}</div>
      <div className="text-2xl font-bold text-gray-900">{value}</div>
    </div>
  );
};

interface CategoryCardProps {
  label: string;
  hours: number;
  target: number;
  color: string;
}

const CategoryCard: React.FC<CategoryCardProps> = ({ label, hours, target, color }) => {
  const percentage = target > 0 ? (hours / target) * 100 : 0;
  const status = percentage >= 90 && percentage <= 110 ? '✅' : percentage > 110 ? '⚠️' : '📈';

  return (
    <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
      <div className="text-sm font-medium text-gray-700 mb-2">{label}</div>
      <div className="text-3xl font-bold text-gray-900 mb-2">
        {Math.round(hours)}
        <span className="text-lg text-gray-500">u</span>
      </div>
      <div className="flex items-center gap-2 text-sm text-gray-600">
        <span>Target: {target}u</span>
        <span>{status}</span>
      </div>
      <div className="mt-3 w-full bg-gray-200 rounded-full h-2 overflow-hidden">
        <div
          className="h-2 rounded-full transition-all duration-500"
          style={{
            width: `${Math.min(percentage, 100)}%`,
            backgroundColor: color
          }}
        />
      </div>
    </div>
  );
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

function formatTaskTime(ms: number): string {
  if (ms < 60000) return '<1m';
  
  const minutes = Math.floor(ms / 60000);
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  
  if (hours > 0) {
    return remainingMinutes > 0 ? `${hours}u ${remainingMinutes}m` : `${hours}u`;
  }
  return `${minutes}m`;
}

function calculateBalanceScore(totals: { work: number; personal: number; self: number; essential: number }): number {
  let score = 0;
  let count = 0;

  Object.entries(totals).forEach(([key, hours]) => {
    const ideal = IDEAL_WEEKLY_BALANCE[key as keyof typeof IDEAL_WEEKLY_BALANCE];
    if (ideal) {
      // Calculate how close to ideal (100 = perfect)
      const deviation = Math.abs(hours - ideal.ideal);
      const maxDeviation = Math.max(ideal.max - ideal.ideal, ideal.ideal - ideal.min);
      const categoryScore = Math.max(0, 100 - (deviation / maxDeviation) * 100);
      score += categoryScore;
      count++;
    }
  });

  return count > 0 ? score / count : 0;
}

export default TimeTrackingDashboard;

